import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

interface CheckoutFormProps {
  priceId: string;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ priceId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);

  const validateForm = () => {
    if (!name.trim()) {
      setError('Please enter your full name');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!cardNumberComplete || !cardExpiryComplete || !cardCvcComplete) {
      setError('Please complete your card details');
      return false;
    }

    if (!postalCode.trim()) {
      setError('Please enter your postal code');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        throw new Error('Card element not found');
      }

      // Create payment method
      console.log('Creating payment method...');
      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: cardNumberElement,
          billing_details: {
            name: name.trim(),
            email: email.trim(),
            address: {
              postal_code: postalCode.trim(),
            },
          },
        });

      if (pmError) {
        console.error('Payment method error:', pmError);
        throw new Error(pmError.message);
      }

      if (!paymentMethod) {
        throw new Error('Payment method creation failed');
      }

      console.log('Payment Method created:', paymentMethod);

      // Call the new unauthenticated subscription function
      console.log('Creating checkout subscription...');
      const createCheckoutSubscription = httpsCallable(
        functions,
        'createCheckoutSubscription'
      );

      const result = await createCheckoutSubscription({
        paymentMethodId: paymentMethod.id,
        priceId,
        email: email.trim(),
        name: name.trim(),
      });

      const subscriptionData = result.data as {
        subscriptionId: string;
        customerId: string;
        status: string;
        currentPeriodEnd: number;
        currentPeriodStart: number;
        subscriptionType: string;
        clientSecret?: string;
        paymentMethod: {
          last4: string;
          brand: string;
          expiryMonth: number;
          expiryYear: number;
        };
      };

      console.log('Subscription created:', subscriptionData);

      // Handle payment authentication if needed
      if (subscriptionData.clientSecret) {
        console.log('Confirming payment...');
        const { error: confirmError } = await stripe.confirmCardPayment(
          subscriptionData.clientSecret
        );

        if (confirmError) {
          console.error('Payment confirmation error:', confirmError);
          throw new Error(confirmError.message);
        }
      }

      // Navigate to account creation with subscription data
      console.log('Payment successful! Navigating to create account...');
      navigate('/create-account', {
        state: {
          paymentMethodId: paymentMethod.id,
          email: email.trim(),
          name: name.trim(),
          priceId,
          subscriptionData,
        },
      });
    } catch (err: any) {
      console.error('Subscription error:', err);
      setError(
        err.message || 'An error occurred while processing your payment'
      );
      setProcessing(false);
    }
  };

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        color: '#1f2937',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#9ca3af',
        },
      },
      invalid: {
        color: '#dc2626',
        iconColor: '#dc2626',
      },
    },
    placeholder: '',
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        {/* Full Name */}
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Full Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={processing}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            placeholder="e.g. John Doe"
            required
          />
        </div>

        {/* Email Address */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={processing}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            placeholder="john@example.com"
            required
          />
        </div>

        {/* Card Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Card Number
          </label>
          <div className="px-4 py-3 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
            <CardNumberElement
              options={{
                ...CARD_ELEMENT_OPTIONS,
                placeholder: '1234 1234 1234 1234',
              }}
              onChange={(e) => setCardNumberComplete(e.complete)}
            />
          </div>
        </div>

        {/* Expiry Date and CVC */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiry Date
            </label>
            <div className="px-4 py-3 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
              <CardExpiryElement
                options={{
                  ...CARD_ELEMENT_OPTIONS,
                  placeholder: 'MM / YY',
                }}
                onChange={(e) => setCardExpiryComplete(e.complete)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CVC
            </label>
            <div className="px-4 py-3 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
              <CardCvcElement
                options={{
                  ...CARD_ELEMENT_OPTIONS,
                  placeholder: 'CVC',
                }}
                onChange={(e) => setCardCvcComplete(e.complete)}
              />
            </div>
          </div>
        </div>

        {/* Postal Code */}
        <div>
          <label
            htmlFor="postalCode"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Postal Code
          </label>
          <input
            type="text"
            id="postalCode"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            disabled={processing}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            placeholder="e.g. 123456"
            required
          />
          <p className="text-xs text-gray-500 mt-1">Enter your postal code</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!stripe || processing}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Processing...
            </span>
          ) : (
            'Subscribe'
          )}
        </button>

        {/* Terms */}
        <p className="text-xs text-gray-500 text-center mt-4">
          By confirming your subscription, you allow Unveil to charge your card
          for this payment and future payments in accordance with their terms.
          You can cancel anytime.
        </p>
      </div>
    </form>
  );
};

export default CheckoutForm;
